import {
	Box,
	ButtonBase,
	Skeleton,
	Stack,
	styled,
	Tooltip,
	tooltipClasses,
	Typography,
} from '@mui/material';
import { useGlobalQuote } from 'contexts/GlobalQuoteContext';

export function getCurrency(country) {
	if (country === 'MEXICO') {
		return 'MXN';
	}
	if (country === 'COLOMBIA') {
		return 'COP';
	}
	if (country === 'BRASIL') {
		return 'BRL';
	}
	return 'ARS';
}

export default function GlobalQuote() {
	const { loading, quote, getQuote } = useGlobalQuote();

	return loading ? (
		<Skeletons />
	) : quote ? (
		<Quote quote={quote} />
	) : (
		<ErrorQuote getQuote={getQuote} />
	);
}

function Skeletons() {
	return (
		<Stack
			spacing="10px"
			direction="row"
			alignItems="center"
			sx={{
				marginTop: '-10px',
			}}
		>
			<Skeleton
				sx={{
					height: '50px',
					width: '60px',
				}}
			/>
			<Skeleton
				sx={{
					height: '50px',
					width: '60px',
				}}
			/>
			<Skeleton
				sx={{
					height: '25px',
					width: '20px',
				}}
			/>
		</Stack>
	);
}

function Quote({ quote }) {
	return (
		<Stack
			spacing={{
				xs: '0px',
				sm: '16px',
			}}
			direction={{ xs: 'column', sm: 'row' }}
			alignItems={{
				xs: 'flex-start',
				sm: 'center',
			}}
		>
			<Typography
				sx={{
					maxWidth: { xs: '60px', md: '60px' },
					color: '#464646',
					fontFamily: 'Roboto',
					fontStyle: 'normal',
					fontWeight: '400',
					fontSize: {
						xs: '8px',
						sm: '10px',
						md: '12px',
					},
					lineHeight: { xs: '10px', md: '16px' },
					letterSpacing: '0.15px',
					textAlign: 'end',
				}}
			>
				Cotización USD
			</Typography>

			<Stack
				direction="row"
				alignItems={{
					xs: 'flex-start',
					sm: 'center',
				}}
				spacing="5px"
			>
				<Typography
					sx={{
						color: '#4A4A4A',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontWeight: '600',
						fontSize: {
							xs: '12px',
							sm: '24px',
							md: '32px',
						},
						lineHeight: '24px',
						letterSpacing: '0.15px',
					}}
				>
					${quote?.tasa.toFixed(2)}
				</Typography>
				<Typography
					sx={{
						maxWidth: '106px',
						color: '#464646',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontWeight: '400',
						fontSize: {
							xs: '8px',
							sm: '10px',
							md: '12px',
						},
						lineHeight: '24px',
						letterSpacing: '0.15px',
						display: { xs: 'none', sm: 'block' },
					}}
				>
					{quote?.moneda}
				</Typography>
			</Stack>
		</Stack>
	);
}

const CustomTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: '#616161E5',
	},
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: '#616161E5',
	},
}));

function ErrorQuote({ getQuote }) {
	return (
		<Stack
			direction="row"
			spacing="8px"
			alignItems="center"
			sx={{
				borderRadius: '4px 8px',
				backgroundColor: '#F0F0F0',
				padding: '8px',
			}}
		>
			<Box
				sx={{
					display: {
						xs: 'none',
						sm: 'block',
					},
				}}
			>
				<MoneyIcon />
			</Box>

			<Stack>
				<Typography
					sx={{
						color: 'rgba(70, 70, 70, 0.90)',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontWeight: '500',
						fontSize: { xs: '10px', sm: '12px' },
						lineHeight: { xs: '12px', sm: '16px' },
						letterSpacing: '0.15px',
						width: { xs: '70px', sm: 'auto' },
					}}
				>
					Cotización no disponible
				</Typography>
				<Typography
					sx={{
						color: 'rgba(70, 70, 70, 0.80)',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontWeight: '300',
						fontSize: '11px',
						lineHeight: '16px',
						letterSpacing: '0.15px',
						display: { xs: 'none', sm: 'block' },
					}}
				>
					Intenta recargar los datos.
				</Typography>
			</Stack>

			<CustomTooltip title="Volver a cargar" arrow>
				<ButtonBase onClick={getQuote}>
					<ReloadIcon />
				</ButtonBase>
			</CustomTooltip>
		</Stack>
	);
}

function MoneyIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
		>
			<title>Cotización</title>

			<path
				d="M3.28 2.22009C3.13783 2.08761 2.94978 2.01549 2.75548 2.01892C2.56118 2.02234 2.37579 2.10106 2.23838 2.23847C2.10097 2.37588 2.02225 2.56127 2.01883 2.75557C2.0154 2.94987 2.08752 3.13792 2.22 3.28009L3.958 5.02009C3.41662 5.09095 2.91948 5.35625 2.55924 5.76653C2.199 6.17682 2.00024 6.7041 2 7.25009V14.7501C2 15.3468 2.23705 15.9191 2.65901 16.3411C3.08097 16.763 3.65326 17.0001 4.25 17.0001H15.94L17.434 18.4951C17.374 18.4984 17.3127 18.5001 17.25 18.5001H4.401C4.66438 18.9563 5.04324 19.3351 5.49948 19.5984C5.95571 19.8617 6.47323 20.0003 7 20.0001H17.25C17.76 20.0001 18.25 19.9201 18.71 19.7711L20.72 21.7811C20.7892 21.8527 20.872 21.9098 20.9635 21.9491C21.055 21.9883 21.1535 22.009 21.2531 22.0098C21.3526 22.0106 21.4514 21.9916 21.5435 21.9538C21.6357 21.9161 21.7194 21.8603 21.7898 21.7899C21.8602 21.7194 21.9158 21.6357 21.9535 21.5435C21.9912 21.4513 22.0101 21.3525 22.0092 21.2529C22.0083 21.1533 21.9875 21.0549 21.9482 20.9635C21.9088 20.872 21.8517 20.7892 21.78 20.7201L3.28 2.22009ZM14.44 15.5001H6.5V14.7501C6.5 14.1534 6.26295 13.5811 5.84099 13.1591C5.41903 12.7371 4.84674 12.5001 4.25 12.5001H3.5V9.50009H4.25C4.79617 9.5001 5.32369 9.30144 5.73418 8.94118C6.14468 8.58091 6.41012 8.08364 6.481 7.54209L8.117 9.17809C7.67448 9.75553 7.45687 10.4741 7.50468 11.2C7.55248 11.926 7.86245 12.6098 8.37687 13.1242C8.8913 13.6386 9.57512 13.9486 10.3011 13.9964C11.027 14.0442 11.7456 13.8266 12.323 13.3841L14.44 15.5001ZM9.197 10.2571L11.243 12.3041C10.9574 12.4669 10.6261 12.5314 10.3003 12.4876C9.9744 12.4438 9.67196 12.2941 9.43947 12.0616C9.20697 11.8291 9.05731 11.5267 9.0135 11.2008C8.96969 10.875 9.03416 10.5437 9.197 10.2581M3.5 7.25009C3.5 7.05118 3.57902 6.86041 3.71967 6.71976C3.86032 6.57911 4.05109 6.50009 4.25 6.50009H5V7.25009C5 7.449 4.92098 7.63977 4.78033 7.78042C4.63968 7.92107 4.44891 8.00009 4.25 8.00009H3.5V7.25009ZM4.25 15.5001C4.05109 15.5001 3.86032 15.4211 3.71967 15.2804C3.57902 15.1398 3.5 14.949 3.5 14.7501V14.0001H4.25C4.44891 14.0001 4.63968 14.0791 4.78033 14.2198C4.92098 14.3604 5 14.5512 5 14.7501V15.5001H4.25ZM16.75 12.5001C16.4449 12.4986 16.1428 12.5606 15.863 12.6821L17.18 14.0001H17.499V14.3201L18.817 15.6371C18.9385 15.3572 19.0005 15.0552 18.999 14.7501V7.25009C18.999 6.65353 18.7621 6.08138 18.3403 5.65945C17.9186 5.23753 17.3466 5.00036 16.75 5.00009H8.18L9.68 6.50009H14.5V7.25009C14.5 7.54556 14.5582 7.83815 14.6713 8.11113C14.7843 8.38411 14.9501 8.63215 15.159 8.84108C15.581 9.26304 16.1533 9.50009 16.75 9.50009H17.5V12.5001H16.75ZM17.5 7.25009V8.00009H16.75C16.5511 8.00009 16.3603 7.92107 16.2197 7.78042C16.079 7.63977 16 7.449 16 7.25009V6.50009H16.75C16.9489 6.50009 17.1397 6.57911 17.2803 6.71976C17.421 6.86041 17.5 7.05118 17.5 7.25009ZM20.062 16.8811L21.147 17.9661C21.685 17.1961 22 16.2601 22 15.2501V10.0001C22.0002 9.47332 21.8616 8.9558 21.5983 8.49957C21.335 8.04333 20.9562 7.66447 20.5 7.40109V15.2501C20.5007 15.8229 20.3496 16.3857 20.062 16.8811Z"
				fill="#1C1B1F"
				fillOpacity="0.2"
			/>
		</svg>
	);
}

function ReloadIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="none"
		>
			<title>Recargar</title>
			<path
				d="M16.6108 10.8675C16.4535 12.0675 15.9724 13.202 15.2193 14.1493C14.4662 15.0967 13.4694 15.8211 12.3358 16.245C11.2022 16.6689 9.97466 16.7763 8.78472 16.5555C7.59479 16.3347 6.4874 15.7942 5.5813 14.9919C4.67521 14.1897 4.0046 13.1558 3.6414 12.0014C3.2782 10.8469 3.23611 9.6154 3.51964 8.43884C3.80317 7.26228 4.40162 6.1851 5.25082 5.32281C6.10002 4.46053 7.16793 3.84567 8.34001 3.54418C11.5892 2.71085 14.9525 4.38335 16.1942 7.50002"
				stroke="#686868"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16.6667 3.33337V7.50004H12.5"
				stroke="#686868"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
